import * as tagLoader from 'OK/react-tag-loader';
import * as webapi from 'OK/webapi';
import * as loginPopupConfigImport from 'OK/pms!loginPopupConfig';
import * as authLoginPopupEventBuses from 'OK/AuthLoginPopupEventBuses';
import { IAuthPopup } from "@ok/interfaces/auth/IAuthPopup";
import { ILoginPopupConfig } from "@ok/interfaces/auth/ILoginPopupConfig";
import { MsgParams } from "@ok/interfaces/auth/IAuthPopupOpenParams";
import { AuthTriggerAction } from "./AuthTypes";
import { parseJsonConf } from "OK/utils/parseJsonConf";

const TAG_NAME = 'auth-login-popup';
const loginPopupConfig = <ILoginPopupConfig>loginPopupConfigImport;

interface IAuthLoginCtx {
    el: HTMLElement | null;
    popupTriggeringAction: AuthTriggerAction | null;
    msgParams: MsgParams | null;
    clickHandler: ((e: MouseEvent) => void) | null;
    stopPropagation: boolean;
}

interface PopupAppNode extends IAuthPopup, HTMLElement {}

const getInstanceData = (
    el: HTMLElement,
    { popupTriggeringAction, msgParams }: Pick<IAuthLoginCtx, 'popupTriggeringAction' | 'msgParams'>,
    stopPropagation: boolean
): IAuthLoginCtx => ({
    el,
    popupTriggeringAction,
    msgParams,
    clickHandler: null,
    stopPropagation,
});

let savedLoginIntentForHref: string | undefined;
let savedHref: string | undefined;
let popupAppNode: PopupAppNode | undefined;
const instanceMap = new Map<HTMLElement, IAuthLoginCtx>();

export const AuthLoginPopup = {
    activate(el: HTMLElement) {
        const ctxData = getInstanceData(el, getMsgParams(el), el.getAttribute('data-stop-propagation') === 'true');
        instanceMap.set(el, ctxData);
        ctxData.clickHandler = this.onClick.bind(ctxData);
        el.addEventListener('click', ctxData.clickHandler);
        AuthLoginPopup.refreshPopupNode();
    },

    deactivate(el: HTMLElement) {
        // если на клик, то не трогаем логику автоматических попапов
        // удаляем только то что привязали на элемент
        // В некоторых окружениях дублируются фиды, из-за чего элемент может деактивироваться несколько раз и поэтому отсутствовать в instanceMap
        el.removeEventListener('click', instanceMap.get(el)?.clickHandler!);
        instanceMap.delete(el);
    },

    layerAlreadyOpened() {
        const authLayerElements = document.getElementsByTagName(TAG_NAME);
        return authLayerElements && authLayerElements.length && authLayerElements[0].hasChildNodes();
    },

    closeIfOpened() {
        try {
            if (AuthLoginPopup.layerAlreadyOpened()) {
                popupAppNode?.close();
            }
        } catch (e) {
        }
    },

    open(params: MsgParams) {
        if (params.href) {
            if (params.href === savedHref) {
                // есть старый, закешированный
                params.loginIntent = savedLoginIntentForHref!;
            } else {
                // сделаем новый loginIntent и откроем с ним леер
                webapi.invoke('login/addLoginIntentForHref', {
                    href: params.href
                }).then((afterLoginIntent) => {
                    params.loginIntent = afterLoginIntent;
                    savedHref = params.href as string;
                    savedLoginIntentForHref = afterLoginIntent;

                    if (AuthLoginPopup.layerAlreadyOpened()) {
                        // ненадо открывать леер дважды
                        return;
                    }

                    AuthLoginPopup.openInternal(params);
                });
                return;
            }
        }
        AuthLoginPopup.openInternal(params);
    },

    openInternal(params: MsgParams) {
        delete params.href; //Say "No!" to "href". Let's use "loginIntent" instead!

        if (popupAppNode && !popupAppNode.parentElement) {
            AuthLoginPopup.refreshPopupNode();
        }
        if (popupAppNode?.parentElement && !loginPopupConfig.isAuthPopupRedesign2023) {
            popupAppNode.open(params);
        } else {
            const container = document.getElementById('hook_Block_LoginPopupReact')!;
            let partCommands;
            if (params.loginIntent) {
                partCommands = {
                    'socialButtons': {
                        commandName: 'login/getSocialLoginButtons',
                        commandParams: {
                            loginIntentToken: params.loginIntent
                        }
                    }
                };
            } else {
                partCommands = {
                    'socialButtons': 'login/getSocialLoginButtons'
                };
            }

            if (loginPopupConfig.isAuthPopupRedesign2023) {
                authLoginPopupEventBuses.AUTH_LOGIN_POPUP_OPEN.emit({
                    ...params,
                    // Fallback на случай если урл был не прокинут
                    goBackAfterLogin: true
                }, this);
            } else {
                tagLoader.getTag<PopupAppNode>(
                    TAG_NAME,
                    partCommands as tagLoader.IParts,
                    container
                ).then((popupAppNodeNew) => {
                    popupAppNode = popupAppNodeNew;
                    popupAppNode.open(params);
                });
            }
        }
    },

    openLayer(el: HTMLElement) {
        if (AuthLoginPopup.layerAlreadyOpened()) {
            // ненадо открывать леер дважды
            return;
        }

        const href = getHref(el);
        const text = el.getAttribute('data-login-layer-text') || '';
        const registrationToken = el.getAttribute('data-login-layer-registration-token') || '';
        const socialApiProvider = el.getAttribute('data-login-layer-social-api-provider') || '';
        const additionalStatLocation = el.getAttribute('data-additional-stat-location') || '';
        const showCloseAsButton = el.getAttribute('data-show-close-as-button') || false;

        const contactOwnerExists = false;
        const landing = el.getAttribute('data-landing-page') === "true";
        const contactOwnerRecoveryRequired = false;

        const showProfilesAttrValue = el.getAttribute('data-login-show-profiles');
        const showProfiles = showProfilesAttrValue === 'true' || showProfilesAttrValue === null;

        const headerImageConf = parseJsonConf(el, 'data-login-layer-header-image-conf');

        const isHobbyShowLanding = el.getAttribute('data-is-hobbyshow-landing') === "true";

        const hideFooter = el.getAttribute('data-login-hide-footer') === 'true';

        const stateParams = parseJsonConf(el, 'data-state-params');

        const loginIntent = el.getAttribute("data-login-intent");

        const params: MsgParams = {
            href: href,
            text: text,
            showProfiles: showProfiles,
            hideFooter: hideFooter,
            registrationToken: registrationToken,
            socialApiProvider: socialApiProvider,
            landing: landing,
            contactOwnerExists: contactOwnerExists,
            contactOwnerRecoveryRequired: contactOwnerRecoveryRequired,
            showCloseAsButton: showCloseAsButton,
            headerImage: headerImageConf,
            additionalStatLocation: additionalStatLocation,
            isHobbyShowLanding: isHobbyShowLanding,
            stateParams: stateParams,
            loginIntent,
            ...getMsgParams(el)
        };

        AuthLoginPopup.open(params);
    },

    refreshPopupNode(): void {
        popupAppNode = document.querySelector(TAG_NAME) as unknown as PopupAppNode;
    },

    onClick(e: MouseEvent) {
        e.preventDefault();

        let el = <HTMLElement>e.target;

        // находим элемент с конфигом, тк клик мог отработать на какой-то из потомков элемента, на который мы повесили onclick
        while (el && el.getAttribute("data-module") !== "AuthLoginPopup") {
            el = el.parentElement!;
        }
        if (!el) {
            // no config
            return;
        }
        if (instanceMap.get(el)?.stopPropagation) {
            e.stopPropagation();
            e.stopImmediatePropagation();
        }

        AuthLoginPopup.openLayer.call(this, el);
    },
};

export const open = AuthLoginPopup.open;
export const openInternal = AuthLoginPopup.openInternal;
export const openLayer = AuthLoginPopup.openLayer;
export const layerAlreadyOpened = AuthLoginPopup.layerAlreadyOpened;
export const closeIfOpened = AuthLoginPopup.closeIfOpened;

export type TAuthLoginPopup = typeof AuthLoginPopup;

export default AuthLoginPopup;

type TMsgParamsThis = { popupTriggeringAction: AuthTriggerAction, msgParams: MsgParams };
function getMsgParams<T extends TMsgParamsThis>(this: T | void, el: HTMLElement) {
    if (this && this?.popupTriggeringAction) {
        return {
            popupTriggeringAction: this.popupTriggeringAction,
            msgParams: this.msgParams,
        };
    }
    let popupTriggeringAction: AuthTriggerAction | null = <AuthTriggerAction>el.getAttribute('data-trigger-action');
    if (popupTriggeringAction && !(popupTriggeringAction in AuthTriggerAction)) {
        console.error('Wrong trigger action value! But be aware that the key is checking! Current value: ', popupTriggeringAction)
        popupTriggeringAction = null;
    }
    const msgParams = <MsgParams>parseJsonConf(el, 'data-msg-params');

    return {
        popupTriggeringAction: popupTriggeringAction,
        msgParams: msgParams
    };
}

const getElHref = (el: HTMLElement) => {
    let href = el.getAttribute("data-href") || el.getAttribute("href") || "";
    if (href === "#") {
        href = "";
    }
    return href;
};

const getCurrentHref = () => {
    return window.location.pathname + window.location.search;
}

function getHref(el: HTMLElement) {
    const goBackAfterLogin = el.getAttribute("data-go-back-after-login") === 'true';
    const saveSearchAttributes = el.getAttribute("data-save-search-attrs") !== 'false';
    const url = goBackAfterLogin ? getCurrentHref() : getElHref(el) || getCurrentHref();

    return saveSearchAttributes ? url : url.split("?")[0];
}
